import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore, getUserById } from '../store';
import { BountyCard } from '../components/missions/BountyCard';
import { MissionDetailModal } from '../components/missions/MissionDetailModal';
import heroBanner from '../assets/hero-banner.jpg';
import { AuthModal } from '../components/auth/AuthModal';
import { ReportModal } from '../components/auth/ReportModal';
import { Mission } from '../types';
import { t } from '../lib/i18n';

export const HomePage: React.FC = () => {
  const { missions, lang } = useStore();
  const [selectedMission, setSelectedMission] = useState<Mission | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ id: string; name: string } | null>(null);
  const navigate = useNavigate();

  const openMissions = missions.filter(m => m.status === 'open');

  const explain: Record<string, { h2: string; p: string }> = {
    en: {
      h2: 'The marketplace for real-world missions',
      p: 'Modern Mercenaries connects people who need things done with talented individuals ready to do them. Post any legal task, set your price, and hire the right person — fast.',
    },
    fr: {
      h2: 'La plateforme pour les missions du quotidien',
      p: 'Modern Mercenaries met en relation des personnes ayant besoin d\'aide avec des talents prêts à agir. Publiez n\'importe quelle mission légale, fixez votre prix et embauchez la bonne personne — rapidement.',
    },
    es: {
      h2: 'El mercado para misiones del mundo real',
      p: 'Modern Mercenaries conecta personas que necesitan ayuda con personas talentosas listas para actuar. Publica cualquier tarea legal, establece tu precio y contrata a la persona adecuada — rápido.',
    },
  };

  const ex = explain[lang] || explain.en;

  return (
    <div>
      {/* HERO */}
      <section
        className="hero"
        style={{
          backgroundImage: `url(${heroBanner})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '280px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(20, 10, 0, 0.42)',
          pointerEvents: 'none',
        }} />
        <div className="hero-inner" style={{
          position: 'relative', zIndex: 1,
          textAlign: 'center',
          padding: '2rem 1rem',
          width: '100%',
          maxWidth: '700px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.75rem',
        }}>
          <h1 style={{
            fontFamily: 'var(--font-western)',
            color: '#fff',
            fontSize: 'clamp(2rem, 6vw, 3.2rem)',
            textShadow: '0 2px 16px rgba(0,0,0,0.7)',
            margin: 0,
            lineHeight: 1.1,
          }}>
            Modern Mercenaries
          </h1>
          <p style={{
            fontFamily: 'var(--font-western)',
            color: '#f5e6c8',
            fontSize: 'clamp(0.85rem, 2.5vw, 1.05rem)',
            textShadow: '0 2px 8px rgba(0,0,0,0.6)',
            margin: 0,
            letterSpacing: '0.03em',
          }}>
            Post missions. Hire people. Get things done.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button className="btn btn-gold" onClick={() => navigate('/missions')}>
              Browse Missions
            </button>
            <button className="btn btn-outline" style={{ color: '#fff', borderColor: '#fff' }} onClick={() => navigate('/missions/create')}>
              Post a Mission
            </button>
          </div>
        </div>
      </section>

      {/* MISSION CAROUSEL */}
      {openMissions.length > 0 && (
        <div className="mission-scroll-wrap">
          <div className="mission-scroll-track">
            {[...openMissions, ...openMissions, ...openMissions].map((m, i) => (
              <BountyCard
                key={m.id + '-' + i}
                mission={m}
                poster={getUserById(m.posterId)}
                onClick={() => setSelectedMission(m)}
              />
            ))}
          </div>
        </div>
      )}

      {/* EXPLAINER */}
      <div style={{
        maxWidth: '700px',
        margin: '2.5rem auto',
        padding: '0 1.5rem',
        textAlign: 'center',
      }}>
        <h2 style={{
          fontFamily: 'var(--font-western)',
          color: '#3d2914',
          fontSize: 'clamp(1.1rem, 3vw, 1.5rem)',
          marginBottom: '0.75rem',
        }}>
          {ex.h2}
        </h2>
        <p style={{
          color: '#6b4a2a',
          fontSize: '0.95rem',
          lineHeight: 1.7,
        }}>
          {ex.p}
        </p>
      </div>

      <MissionDetailModal
        mission={selectedMission}
        open={!!selectedMission}
        onClose={() => setSelectedMission(null)}
        onOpenAuth={() => setAuthOpen(true)}
        onReport={(id, name) => setReportTarget({ id, name })}
      />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <ReportModal
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        targetId={reportTarget?.id ?? null}
        targetName={reportTarget?.name}
      />
    </div>
  );
};
